import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    Chip,
    CircularProgress,
    Alert,
    CardActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    Star as StarIcon,
    Check as CheckIcon,
    CreditCard as CreditCardIcon,
    SwapHoriz as SwapHorizIcon,
    TrendingUp as TrendingUpIcon,
    Business as BusinessIcon,
    Rocket as RocketIcon
} from '@mui/icons-material';
import subscriptionApi from '../services/subscriptionApi';
import PlanChangePreview from './PlanChangePreview';
import PlanChangeStatus from './PlanChangeStatus';
import PaymentFailedModal from './PaymentFailedModal';
import * as Sentry from "@sentry/react";
import { normalizeSubscriptionStatus } from '../utils/subscriptionUtils';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(amount / 100); // Convert cents to dollars
};

const formatCredits = (credits) => {
    return new Intl.NumberFormat('en-US').format(credits);
};

const formatPricePerCredit = (priceCents, credits) => {
    const pricePerCredit = priceCents / credits;
    return `$${(pricePerCredit / 100).toFixed(3)}`;
};

const getDisplayPlanName = (planName) => {
    const name = planName.toLowerCase();
    if (name.includes('basic')) return 'Basic';
    if (name.includes('pro') && !name.includes('test')) return 'Growth';
    if (name.includes('enterprise')) return 'Pro';
    return planName; // fallback to original name
};

const SubscriptionPlans = ({ onSubscriptionSelect, userSubscriptions = [], loading: parentLoading = false }) => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [subscribing, setSubscribing] = useState(null);
    
    // New state for enhanced plan change flow
    const [previewDialog, setPreviewDialog] = useState(null);
    const [statusDialog, setStatusDialog] = useState(null);
    const [failedDialog, setFailedDialog] = useState(null);
    const [planChangeId, setPlanChangeId] = useState(null);

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const plansData = await subscriptionApi.getSubscriptionPlans();
            // Filter out Test Pro Plan and any test plans
            const filteredPlans = plansData.filter(plan => 
                !plan.name.toLowerCase().includes('test')
            );
            setPlans(filteredPlans);
        } catch (error) {
            Sentry.captureException(error);
            setError('Failed to load subscription plans');
            console.error('Error fetching plans:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubscribe = async (plan) => {
        try {
            setSubscribing(plan.id);
            
            // Create checkout session and redirect to Stripe
            const checkoutResponse = await subscriptionApi.createSubscriptionCheckout(plan.id);
            
            // Redirect to Stripe checkout
            window.location.href = checkoutResponse.checkout_url;
            
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error creating subscription checkout:', error);
            setError(error.message || 'Failed to start subscription process');
            setSubscribing(null);
        }
    };

    const handlePlanChange = (newPlan) => {
        // Get current active subscription
        const currentSubscription = userSubscriptions.find(sub => {
            const status = normalizeSubscriptionStatus(sub.status);
            return (status === 'active' || status === 'trialing');
        });
        
        if (currentSubscription) {
            // Use the plan object directly from the subscription if available, 
            // otherwise find it in the plans array
            const currentPlan = currentSubscription.plan || 
                               plans.find(p => p.id === (currentSubscription.subscription_plan_id || currentSubscription.plan?.id));
            setPreviewDialog({
                newPlan,
                currentPlan,
                currentSubscription
            });
        } else {
            // No active subscription, proceed with normal subscription
            handleSubscribe(newPlan);
        }
    };

    const handlePreviewConfirm = async (previewData) => {
        if (!previewDialog) return;
        
        try {
            setSubscribing(previewDialog.newPlan.id);
            setPreviewDialog(null);
            
            // Create plan change using new API
            const planChangeResponse = await subscriptionApi.createPlanChange(previewDialog.newPlan.id);
            const planChangeId = planChangeResponse.plan_change_id || planChangeResponse.id;
            setPlanChangeId(planChangeId);
            
            // Check if plan change completed immediately
            if (planChangeResponse.status === 'completed') {
                // Plan change completed immediately (test mode), refresh subscriptions
                setSubscribing(null);
                if (onSubscriptionSelect) {
                    onSubscriptionSelect();
                }
                return;
            }
            
            // Show status tracking dialog for pending/processing states
            setStatusDialog({
                planChangeId: planChangeId,
                newPlan: previewDialog.newPlan,
                currentPlan: previewDialog.currentPlan
            });
            
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error creating plan change:', error);
            setError(error.message || 'Failed to initiate plan change');
            setSubscribing(null);
        }
    };

    const handleStatusComplete = (statusData) => {
        // Plan change completed successfully
        setStatusDialog(null);
        setSubscribing(null);
        setPlanChangeId(null);
        
        // Refresh subscriptions
        if (onSubscriptionSelect) {
            onSubscriptionSelect();
        }
        
        // Show success message
        setError(null);
    };

    const handleStatusError = (statusData) => {
        // Plan change failed
        setStatusDialog(null);
        
        // Show payment failed modal
        setFailedDialog({
            planChangeRequest: statusData,
            newPlan: statusDialog?.newPlan,
            currentPlan: statusDialog?.currentPlan
        });
    };

    const handleRetryFromStatus = () => {
        // Restart status polling after retry
        // Status component handles the retry internally
    };

    const handleRetryFromFailed = () => {
        // Close failed dialog and restart status tracking
        setFailedDialog(null);
        
        if (planChangeId) {
            setStatusDialog({
                planChangeId,
                newPlan: failedDialog?.newPlan,
                currentPlan: failedDialog?.currentPlan
            });
        }
    };

    const handleUpdatePaymentMethod = () => {
        // Close failed dialog
        setFailedDialog(null);
        setSubscribing(null);
        setPlanChangeId(null);
    };

    const isActivePlan = (planId) => {
        return userSubscriptions.some(sub => {
            const status = normalizeSubscriptionStatus(sub.status);
            return (sub.subscription_plan_id === planId || sub.plan?.id === planId) && 
                   (status === 'active' || status === 'trialing');
        });
    };

    const hasActiveSubscription = () => {
        return userSubscriptions.some(sub => {
            const status = normalizeSubscriptionStatus(sub.status);
            return (status === 'active' || status === 'trialing');
        });
    };

    const getPlanConfig = (planName) => {
        const name = planName.toLowerCase();
        if (name.includes('basic')) {
            return { 
                badge: { label: 'STARTER', color: 'success' },
                icon: <RocketIcon />,
                gradient: 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)'
            };
        }
        if (name.includes('pro') && !name.includes('test')) {
            return { 
                badge: { label: 'POPULAR', color: 'primary' },
                icon: <TrendingUpIcon />,
                gradient: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)'
            };
        }
        if (name.includes('enterprise')) {
            return { 
                badge: { label: 'ENTERPRISE', color: 'error' },
                icon: <BusinessIcon />,
                gradient: 'linear-gradient(135deg, #ab47bc 0%, #7b1fa2 100%)'
            };
        }
        return { 
            badge: null, 
            icon: <CreditCardIcon />,
            gradient: 'linear-gradient(135deg, #90a4ae 0%, #546e7a 100%)'
        };
    };

    const getPlanFeatures = (planName, credits, priceCents) => {
        const pricePerCredit = formatPricePerCredit(priceCents, credits);
        return [
            `${formatCredits(credits)} credits/month`,
            `${pricePerCredit} per credit`
        ];
    };

    if (loading || parentLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    if (plans.length === 0) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                No subscription plans available at the moment.
            </Alert>
        );
    }

    return (
        <Box>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Typography variant="h4" sx={{ mb: 2, fontWeight: 700, color: 'text.primary' }}>
                    Choose Your Plan
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                    Scale your AI operations with flexible pricing that grows with your business
                </Typography>
            </Box>
            
            <Grid container spacing={3} justifyContent="center">
                {plans.map((plan) => {
                    const displayName = getDisplayPlanName(plan.name);
                    const config = getPlanConfig(plan.name);
                    const features = getPlanFeatures(plan.name, plan.monthly_credits, plan.price_cents);
                    const isActive = isActivePlan(plan.id);
                    const isLoading = subscribing === plan.id;

                    return (
                        <Grid item xs={12} sm={6} md={4} key={plan.id}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    border: isActive ? '3px solid #1976d2' : '1px solid rgba(0,0,0,0.12)',
                                    borderRadius: 3,
                                    background: isActive ? 
                                        'linear-gradient(135deg, rgba(25,118,210,0.05) 0%, rgba(25,118,210,0.1) 100%)' : 
                                        'background.paper',
                                    boxShadow: isActive ? 
                                        '0 8px 25px rgba(25,118,210,0.3)' : 
                                        '0 2px 10px rgba(0,0,0,0.1)',
                                    transition: 'all 0.3s ease',
                                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                                    '&:hover': {
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                        transform: isActive ? 'scale(1.02)' : 'scale(1.01)'
                                    }
                                }}
                            >
                                {/* Header with gradient */}
                                <Box 
                                    sx={{ 
                                        background: config.gradient,
                                        color: 'white',
                                        p: 3,
                                        borderRadius: '12px 12px 0 0',
                                        position: 'relative'
                                    }}
                                >
                                    {config.badge && (
                                        <Chip 
                                            label={config.badge.label} 
                                            size="small"
                                            sx={{ 
                                                position: 'absolute',
                                                top: 12,
                                                right: 12,
                                                background: 'rgba(255,255,255,0.2)',
                                                color: 'white',
                                                fontWeight: 600,
                                                backdropFilter: 'blur(10px)'
                                            }}
                                        />
                                    )}

                                    {isActive && (
                                        <Chip 
                                            label="ACTIVE" 
                                            size="small"
                                            icon={<StarIcon sx={{ color: 'white !important' }} />}
                                            sx={{ 
                                                position: 'absolute',
                                                top: 12,
                                                left: 12,
                                                background: 'rgba(76, 175, 80, 0.9)',
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                    )}

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pt: isActive || config.badge ? 2 : 0 }}>
                                        {config.icon}
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                            {displayName}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                        <Typography variant="h3" sx={{ fontWeight: 800 }}>
                                            {formatCurrency(plan.price_cents)}
                                        </Typography>
                                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                            /month
                                        </Typography>
                                    </Box>
                                </Box>

                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <List dense sx={{ p: 0 }}>
                                        {features.map((feature, index) => (
                                            <ListItem key={index} sx={{ px: 0, py: 1 }}>
                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                    <CheckIcon 
                                                        sx={{ 
                                                            color: 'success.main',
                                                            background: 'rgba(76, 175, 80, 0.1)',
                                                            borderRadius: '50%',
                                                            p: 0.5
                                                        }} 
                                                    />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={feature}
                                                    primaryTypographyProps={{
                                                        variant: 'body1',
                                                        fontWeight: 500,
                                                        color: 'text.primary'
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>

                                <CardActions sx={{ p: 3, pt: 0 }}>
                                    <Button
                                        fullWidth
                                        variant={isActive ? "outlined" : "contained"}
                                        color={isActive ? "success" : "primary"}
                                        size="large"
                                        onClick={() => {
                                            if (isActive) {
                                                // Do nothing for active plan
                                                return;
                                            } else if (hasActiveSubscription()) {
                                                // Show confirmation dialog for plan change
                                                handlePlanChange(plan);
                                            } else {
                                                // No active subscription, proceed with normal subscription
                                                handleSubscribe(plan);
                                            }
                                        }}
                                        disabled={isActive || isLoading}
                                        startIcon={
                                            isLoading ? <CircularProgress size={20} color="inherit" /> :
                                            isActive ? <StarIcon /> :
                                            hasActiveSubscription() ? <SwapHorizIcon /> :
                                            <CreditCardIcon />
                                        }
                                        sx={{ 
                                            py: 1.5,
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            background: isActive ? 
                                                'transparent' : 
                                                'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                            '&:hover': {
                                                background: isActive ? 
                                                    'rgba(76, 175, 80, 0.1)' : 
                                                    'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
                                            }
                                        }}
                                    >
                                        {isLoading ? 'Processing...' : 
                                         isActive ? 'Current Plan' : 
                                         hasActiveSubscription() ? 'Switch to This Plan' :
                                         'Get Started'}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Plan Change Preview Dialog */}
            <PlanChangePreview
                open={!!previewDialog}
                onClose={() => !subscribing && setPreviewDialog(null)}
                currentPlan={previewDialog?.currentPlan}
                newPlan={previewDialog?.newPlan}
                onConfirm={handlePreviewConfirm}
                loading={subscribing}
            />

            {/* Plan Change Status Tracking Dialog */}
            <PlanChangeStatus
                open={!!statusDialog}
                onClose={() => setStatusDialog(null)}
                planChangeId={statusDialog?.planChangeId}
                onComplete={handleStatusComplete}
                onError={handleStatusError}
                onRetry={handleRetryFromStatus}
            />

            {/* Payment Failed Modal */}
            <PaymentFailedModal
                open={!!failedDialog}
                onClose={() => setFailedDialog(null)}
                planChangeRequest={failedDialog?.planChangeRequest}
                onRetry={handleRetryFromFailed}
                onUpdatePaymentMethod={handleUpdatePaymentMethod}
            />
        </Box>
    );
};

export default SubscriptionPlans; 